import { useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  ColorLockedLegendItem,
  addToLockedColor,
  removeLockedColor
} from "store/features";
import { RootState } from "store/rootReducer";

import { IColorPalette } from "models/colorPalette";

import useRefreshPreferredColorsInStore from "components/user-settings/hooks/refreshPreferredColorsInStore";

import { GroupByFilterDependencies } from "../types";
import { ColorLockAction, resolveColorLock } from "../util/resolveColorLock";

// EVA-6412 RESHAPE (T017) — consumes the resolved `deps` object instead of
// `entityKind`; drops the internal entityKind === Facility branch (fixes P2).
export interface ColorLockControllerResult {
  lockedColorsInStore: Record<string, ColorLockedLegendItem>;
  colorLockItem: (item: { property: string; value: string; color: string }) => void;
  activePalette: IColorPalette;
  handlePaletteSelection: (p: IColorPalette) => void;
}

export const useColorLockController = (
  deps: GroupByFilterDependencies
): ColorLockControllerResult => {
  const dispatch = useDispatch();
  const refreshPreferredColorsInStore = useRefreshPreferredColorsInStore();

  const lockedColorsInStore = useSelector((state: RootState) => state.app.lockedColors);
  // Ref so colorLockItem can read the latest locked colors without being recreated when
  // lockedColorsInStore changes. Adding lockedColorsInStore to colorLockItem's deps would
  // give it a new identity on every lock/unlock, causing Legend's memoized Row to re-render
  // and remount the color picker — closing it mid-interaction. NOT a mirror-ref-to-delete
  // (P5 exception, documented in research.md R3).
  const lockedColorsRef = useRef(lockedColorsInStore);
  lockedColorsRef.current = lockedColorsInStore;

  const colorLockItem = useCallback(
    (item: { property: string; value: string; color: string }) => {
      const { action, payload } = resolveColorLock(item, lockedColorsRef.current);

      if (action === ColorLockAction.Remove) {
        dispatch(removeLockedColor(payload));
      } else {
        dispatch(addToLockedColor(payload));
      }
    },
    []
  );

  const { activePalette, isWell, setActiveColorPalette } = deps;

  const handlePaletteSelection = useCallback(
    (p: IColorPalette) => {
      setActiveColorPalette(p);
      if (isWell) {
        refreshPreferredColorsInStore(p);
      }
    },
    [setActiveColorPalette, isWell, refreshPreferredColorsInStore]
  );

  return { lockedColorsInStore, colorLockItem, activePalette, handlePaletteSelection };
};
